/** DECUPAREA PLANȘEI, înainte de citire.
 *
 *  O planșă vine rar doar cu planul: are cartuș, legendă, alte niveluri, o
 *  secțiune pe margine. Cititorul de pe server le-ar citi pe toate — ziduri din
 *  secțiune, camere din legendă —, deci omul arată întâi CE e modulul. Tot ce
 *  rămâne în afara dreptunghiului nu pleacă deloc la server.
 *
 *  Dreptunghiul se trage de colțuri și de laturi, se mută de mijloc, iar o
 *  tragere pe poza din afara lui desenează unul nou (și prin el, cât e încă
 *  toată imaginea). E ținut în fracțiuni din
 *  imagine, fiindcă poza de pe ecran e micșorată (v. `decupeaza`).
 */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent as PE } from "react";
import { DECUPAJ_INTREG, eIntreg, type Decupaj } from "./calc";
import { useT } from "../../i18n";

type Prinsa = "muta" | "nou" | `${"n" | "s" | ""}${"v" | "e" | ""}`;

const MIN = 0.02;                                   // latura cea mai mică, din imagine

const limitat = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

export default function DecupajImagine({ fisier, laGata, laRenunt }: {
  fisier: File;
  laGata: (d: Decupaj) => void;
  laRenunt: () => void;
}) {
  const t = useT();
  const [url, setUrl] = useState("");
  const [d, setD] = useState<Decupaj>(DECUPAJ_INTREG);
  const [px, setPx] = useState<{ w: number; h: number } | null>(null);
  const cadru = useRef<HTMLDivElement | null>(null);
  const gest = useRef<{
    prinsa: Prinsa; x0: number; y0: number; d0: Decupaj; inainte: Decupaj;
  } | null>(null);

  useEffect(() => {
    const u = URL.createObjectURL(fisier);
    setUrl(u);
    setD(DECUPAJ_INTREG);
    return () => URL.revokeObjectURL(u);
  }, [fisier]);

  /** Punctul ecranului, în fracțiuni din poză. */
  const punct = (e: PE) => {
    const r = cadru.current!.getBoundingClientRect();
    return { x: limitat((e.clientX - r.left) / (r.width || 1), 0, 1),
             y: limitat((e.clientY - r.top) / (r.height || 1), 0, 1) };
  };

  const apuca = (cerut: Prinsa) => (e: PE) => {
    // Toată imaginea n-are unde se muta: tragerea prin ea desenează decupajul.
    const prinsa = cerut === "muta" && eIntreg(d) ? "nou" : cerut;
    e.stopPropagation();
    e.preventDefault();
    cadru.current?.setPointerCapture?.(e.pointerId);
    const p = punct(e);
    gest.current = { prinsa, x0: p.x, y0: p.y, inainte: d,
                     d0: prinsa === "nou" ? { x: p.x, y: p.y, w: 0, h: 0 } : d };
  };

  const trage = (e: PE) => {
    const g = gest.current;
    if (!g) return;
    const p = punct(e);
    const { d0 } = g;
    if (g.prinsa === "nou") {
      setD({ x: Math.min(g.x0, p.x), y: Math.min(g.y0, p.y),
             w: Math.abs(p.x - g.x0), h: Math.abs(p.y - g.y0) });
      return;
    }
    if (g.prinsa === "muta") {
      setD({ ...d0,
             x: limitat(d0.x + p.x - g.x0, 0, 1 - d0.w),
             y: limitat(d0.y + p.y - g.y0, 0, 1 - d0.h) });
      return;
    }
    let x1 = d0.x, y1 = d0.y, x2 = d0.x + d0.w, y2 = d0.y + d0.h;
    if (g.prinsa.includes("v")) x1 = limitat(p.x, 0, x2 - MIN);
    if (g.prinsa.includes("e")) x2 = limitat(p.x, x1 + MIN, 1);
    if (g.prinsa.startsWith("n")) y1 = limitat(p.y, 0, y2 - MIN);
    if (g.prinsa.startsWith("s")) y2 = limitat(p.y, y1 + MIN, 1);
    setD({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
  };

  const lasa = () => {
    const g = gest.current;
    gest.current = null;
    // Un clic fără tragere pe poză nu șterge decupajul.
    if (g?.prinsa === "nou") setD((c) => (c.w < MIN || c.h < MIN ? g.inainte : c));
  };

  const pct = (v: number) => `${v * 100}%`;
  const maner = (p: Prinsa) => (
    <span key={p} className={`ob-dec-maner ob-dec-${p}`} onPointerDown={apuca(p)} />
  );

  return (
    <div className="ob-dec">
      <div className="ob-dec-scena">
        <div ref={cadru} className="ob-dec-cadru"
             onPointerDown={apuca("nou")} onPointerMove={trage}
             onPointerUp={lasa} onPointerCancel={lasa}>
          {url && (
            <img src={url} alt="" draggable={false}
                 onLoad={(e) => setPx({ w: e.currentTarget.naturalWidth,
                                        h: e.currentTarget.naturalHeight })} />
          )}
          <div className="ob-dec-sel"
               style={{ left: pct(d.x), top: pct(d.y), width: pct(d.w), height: pct(d.h) }}
               onPointerDown={apuca("muta")}>
            {(["n", "s", "v", "e", "nv", "ne", "sv", "se"] as Prinsa[]).map(maner)}
          </div>
        </div>
      </div>
      <div className="ob-dec-jos">
        <span className="ob-tr-nou-nota">
          {px
            ? t("nou.decupaj_px", { w: Math.round(d.w * px.w), h: Math.round(d.h * px.h) })
            : t("nou.decupaj_nota")}
        </span>
        <button type="button" className="btn" onClick={() => setD(DECUPAJ_INTREG)}>
          {t("nou.decupaj_tot")}
        </button>
        <button type="button" className="btn" onClick={laRenunt}>
          {t("comun.renunta")}
        </button>
        <button type="button" className="btn btnp" onClick={() => laGata(d)}>
          {t("nou.decupaj_gata")}
        </button>
      </div>
    </div>
  );
}
